import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {PlatformEvent, PlatformEventType} from '../models/information';
import {DevicesService, LangService, LoaderService} from '@satoris/ngx-satoris';
import {FormGroup} from '@angular/forms';
import {Results} from '../models/backend-message';

@Injectable({
  providedIn: 'root'
})
export class ActivityService {
  activities: PlatformEvent[] = [];
  filterActivities: PlatformEvent[];
  limit = 10;
  totalActivities = 0;
  offset = 0;
  currentType: 'account' | 'shared';
  contexts: {[key: string]: object} = {};
  claimTypes = new Map;
  isWeb: boolean;

  constructor(private api: ApiService, private loader: LoaderService, private lang: LangService, private devices: DevicesService) {
    this.isWeb = !this.devices.isDevices('cordova');
  }

  getActivities(type: 'account' | 'shared', simpleMode = false, limit = this.isWeb ? 10000 : 10) {
    this.currentType = type;
    this.activities = [];
    this.offset = 0;
    this.limit = limit;
    this.contexts = {};
    this.claimTypes.clear?.();

    const baseExcludeTypes = [PlatformEventType.VOUCHER_PAYMENT].concat(type === 'account'  ?
      [PlatformEventType.CLAIM_CREATED, PlatformEventType.CLAIM_REVOKED, PlatformEventType.CLAIM_FETCHED, PlatformEventType.CLAIM_READ_ALL, PlatformEventType.CLAIM_SHARED]:
      [PlatformEventType.ACCOUNT_CREATED, PlatformEventType.ACCOUNT_CONFIRMED, PlatformEventType.ACCOUNT_CHANGED_PASSWORD, PlatformEventType.ACCOUNT_LOGIN_OK, PlatformEventType.ACCOUNT_CERT_REVOKED, PlatformEventType.ACCOUNT_CHANGED_PIN, PlatformEventType.CLAIM_CREATED, PlatformEventType.CLAIM_REVOKED, PlatformEventType.CLAIM_FETCHED, PlatformEventType.CLAIM_SHARED]);

    const extraExcludeTypes = simpleMode ? [0, 1] : [];

    return Promise.all([
      this.api.events(false, this.offset, this.limit),
      this.api.events(true, this.offset, this.limit)
    ]).then(([res1, res2]: [Results<PlatformEvent[]>, Results<PlatformEvent[]>]) => {
      const allActivities: PlatformEvent[] = (res1.result || []).concat(res2.result || []).sort((a, b) => a.id > b.id ? -1 : a.id < b.id ? 1 : 0);

      let filtered = allActivities.filter(activity => ![...extraExcludeTypes, ...baseExcludeTypes].includes((activity?.type as any)));

      if(simpleMode) filtered = this.mergeEvents(filtered);

      const getCount = (res: any) => typeof res.count === 'number' ? res.count : Array.isArray(res.result) ? res.result.length : 0;
      this.totalActivities = getCount(res1) + getCount(res2);

      this.activities = filtered;
      return this.activities;
    }).catch(err => {
      this.loader.loading(true, {type: 'error', message: String(err).indexOf(': ') > -1 ? err : this.lang.transform('activity.getActivities.error')});
      return Promise.reject(err);
    });
  }

  loadMoreActivities(): Promise<boolean> {
    if(this.activities.length >= this.totalActivities) return Promise.resolve(false);

    this.offset += this.limit;
    const excludeTypes = [PlatformEventType.VOUCHER_PAYMENT].concat(this.currentType === 'account'  ?
      [PlatformEventType.CLAIM_CREATED, PlatformEventType.CLAIM_REVOKED, PlatformEventType.CLAIM_FETCHED, PlatformEventType.CLAIM_READ_ALL, PlatformEventType.CLAIM_SHARED]:
      [PlatformEventType.ACCOUNT_CREATED, PlatformEventType.ACCOUNT_CONFIRMED, PlatformEventType.ACCOUNT_CHANGED_PASSWORD, PlatformEventType.ACCOUNT_LOGIN_OK, PlatformEventType.ACCOUNT_CERT_REVOKED, PlatformEventType.ACCOUNT_CHANGED_PIN, PlatformEventType.CLAIM_CREATED, PlatformEventType.CLAIM_REVOKED, PlatformEventType.CLAIM_FETCHED, PlatformEventType.CLAIM_SHARED]);

    return Promise.all([
      this.api.events(false, this.offset, this.limit),
      this.api.events(true, this.offset, this.limit)
    ]).then(([res1, res2]: [Results<PlatformEvent[]>, Results<PlatformEvent[]>]) => {
      const newActivities = (res1.result || []).concat(res2.result || []).sort((a, b) => a.id > b.id ? -1 : a.id < b.id ? 1 : 0);
      const filtered = newActivities.filter((activity: PlatformEvent) => !excludeTypes.includes(activity?.type));

      this.activities = [...this.activities, ...filtered];
      return true;
    }).catch(err => {
      this.offset -= this.limit;
      this.loader.loading(true, {type: 'error', message: String(err)});
      return false;
    });
  }

  mergeEvents(events: PlatformEvent[]): PlatformEvent[] {
    const groupedEvents: PlatformEvent[] = [];
    let currentGroup: any = [];
    let previousContextId = '';

    events.forEach((event: PlatformEvent) => {
      if(event.type === 10){
        let currentContextId;
        try {
          currentContextId = JSON.parse(event.context).id;
        } catch {
          return;
        }
        if(currentContextId === previousContextId) {
          if(currentGroup.length > 0) {
            const groupTypes = [...new Set(currentGroup.map((item: any) => item.sharedTypes))];
            // const groupTypes = currentGroup.map((item: any) => item.sharedTypes);
            const groupTypesTranslated = currentGroup.map((item: any) => this.lang.transform(`activity.claim.simpletype.DYN${item.sharedTypes}`));
            groupedEvents.push({...currentGroup[0], sharedTypes: groupTypes, sharedTypesTranslated: groupTypesTranslated});
          }
          currentGroup = [];
          currentGroup.push({...event, sharedTypes: this.claimTypes.get(currentContextId)});
          previousContextId = currentContextId;
        } else {
          currentGroup.push({...event, sharedTypes: this.claimTypes.get(currentContextId)});
          previousContextId = currentContextId;
        }
      } else if(event.type === 9) {
        if(currentGroup.length > 0) {
          const groupTypes = [...new Set(currentGroup.map((item: any) => item.sharedTypes))];
          // const groupTypes = currentGroup.map((item: any) => item.sharedTypes);
          const groupTypesTranslated = currentGroup.map((item: any) => this.lang.transform(`activity.claim.simpletype.DYN${item.sharedTypes}`));
          groupedEvents.push({...currentGroup[0], sharedTypes: groupTypes, sharedTypesTranslated: groupTypesTranslated});
        }
        currentGroup = [];
        previousContextId = '';
      } else {
        groupedEvents.push(event);
      }
    });

    return groupedEvents;
  }


  onDateRangeChanged(eventData: { dateFrom: string, dateTo: string }) {
    const isDateBetween = (dateFrom: string, dateTo: string) => (item: PlatformEvent) => {
      const df = Date.parse(dateFrom);
      const dt = Date.parse(dateTo);
      const idate = Date.parse(item.updatedOn);

      return idate > df && idate < dt;
    };

    if(eventData.dateFrom === '' && eventData.dateTo === '') {
      this.filterActivities = undefined;
    } else {
      const fdate = eventData.dateFrom === '' ? new Date(+0).toISOString() : eventData.dateFrom;
      const tdate = eventData.dateTo === '' ? new Date().toISOString() : eventData.dateTo;
      this.filterActivities = this.activities.filter(isDateBetween(fdate, tdate));
    }
  }

  submit(form: FormGroup) {
    if(form.get('search').value) {
      const value = form.get('search').value.toLowerCase().trim();
      this.filterActivities = this.activities.filter(x => x.place_id?.toLowerCase().includes(value) || x.target_id?.toString().includes(value) || x.id?.toString().includes(value));
    } else {
      this.filterActivities = undefined;
    }
  }

  resetActivities() {
    this.filterActivities = undefined;
    this.activities = [];
    this.limit = 10;
    this.totalActivities = 0;
  }
}
